import React from "react";
import {
  Car,
  BatteryCharging,
  Lightbulb,
  Droplets,
  Paintbrush,
  Gauge,
  Truck,
  Disc,
  ShieldCheck,
  ShoppingBag,
  Bike,
  Package,
  Flame,
  Settings,
  ToolCase,
  Wrench,
  Fuel,
  Snowflake,
  Wind,
  Anchor,
  Tent,
  Users,
  Award,
  Sparkles,
  Radio,
  Plug,
  Sun,
  Umbrella,
  Layers,
} from "lucide-react";

/* ================= TYPES ================= */
export interface CategoryItem {
  id: number;
  name: string;
  icon: React.ComponentType<{ size?: number; color?: string }>;
  parentId?: number | null;
  color?: string;
  image?: any;
}

/* ──────────────────────────────────────────────
   1️⃣ TOP-LEVEL AUTOMOTIVE CATEGORIES (1300000xx)
   ────────────────────────────────────────────── */
export const automotiveCategories: CategoryItem[] = [
  { id: 130000001, name: "Car Care", icon: Droplets, color: "#1E88E5" },
  { id: 130000002, name: "Car Electronics & Accessories", icon: BatteryCharging, color: "#6D4C41" },
  { id: 130000003, name: "Exterior Accessories", icon: Car, color: "#455A64" },
  { id: 130000004, name: "Interior Accessories", icon: Package, color: "#546E7A" },
  { id: 130000005, name: "Lights & Lighting Accessories", icon: Lightbulb, color: "#F9A825" },
  { id: 130000006, name: "Motorcycle & Powersports", icon: Bike, color: "#C62828" },
  { id: 130000007, name: "Oils & Fluids", icon: Droplets, color: "#2E7D32" },
  { id: 130000008, name: "Paint & Paint Supplies", icon: Paintbrush, color: "#8E24AA" },
  { id: 130000009, name: "Performance Parts & Accessories", icon: Gauge, color: "#D32F2F" },
  { id: 130000010, name: "Replacement Parts", icon: Settings, color: "#455A64" },
  { id: 130000011, name: "RV Parts & Accessories", icon: Truck, color: "#5D4037" },
  { id: 130000012, name: "Tires & Wheels", icon: Disc, color: "#263238" },
  { id: 130000013, name: "Tools & Equipment", icon: ToolCase, color: "#F57C00" },
  { id: 130000014, name: "Automotive Enthusiast Merchandise", icon: ShoppingBag, color: "#6A1B9A" },
  { id: 130000015, name: "Heavy Duty & Commercial Vehicle Equipment", icon: Truck, color: "#37474F" },
  { id: 130000016, name: "Amazon Autos", icon: ShieldCheck, color: "#2E7D32" },
  // ── newly added top-level categories ──
  { id: 130000017, name: "Towing & Trailer Equipment", icon: Anchor, color: "#4E342E" },
  { id: 130000018, name: "Fuel Systems & Additives", icon: Fuel, color: "#EF6C00" },
  { id: 130000019, name: "Climate & Comfort (HVAC)", icon: Snowflake, color: "#0288D1" },
  { id: 130000020, name: "Batteries & Charging Systems", icon: Plug, color: "#FBC02D" },
];

/* ──────────────────────────────────────────────
   2️⃣ SUBCATEGORIES (1300001xx)
   ────────────────────────────────────────────── */
export const automotiveSubcategories: CategoryItem[] = [
  /* Car Care */
  { id: 130000101, parentId: 130000001, name: "Car Wash & Shampoo", icon: Droplets },
  { id: 130000102, parentId: 130000001, name: "Wax & Polish", icon: Droplets },
  { id: 130000103, parentId: 130000001, name: "Interior Cleaners", icon: Package },
  { id: 130000104, parentId: 130000001, name: "Detailing Kits", icon: Sparkles },
  { id: 130000105, parentId: 130000001, name: "Microfiber Towels & Applicators", icon: Layers },

  /* Electronics */
  { id: 130000201, parentId: 130000002, name: "Car Stereos", icon: Radio },
  { id: 130000202, parentId: 130000002, name: "Dash Cams", icon: Gauge },
  { id: 130000203, parentId: 130000002, name: "Car Chargers & Holders", icon: BatteryCharging },
  { id: 130000204, parentId: 130000002, name: "Backup Cameras & Sensors", icon: Gauge },
  { id: 130000205, parentId: 130000002, name: "GPS & Navigation", icon: Radio },

  /* Exterior */
  { id: 130000301, parentId: 130000003, name: "Car Covers", icon: Car },
  { id: 130000302, parentId: 130000003, name: "Roof Racks", icon: Truck },
  { id: 130000303, parentId: 130000003, name: "Mud Flaps", icon: Car },
  { id: 130000304, parentId: 130000003, name: "Bumpers & Body Kits", icon: Car },
  { id: 130000305, parentId: 130000003, name: "Window Visors & Deflectors", icon: Wind },

  /* Interior */
  { id: 130000401, parentId: 130000004, name: "Seat Covers", icon: Package },
  { id: 130000402, parentId: 130000004, name: "Floor Mats", icon: Package },
  { id: 130000403, parentId: 130000004, name: "Steering Covers", icon: Package },
  { id: 130000404, parentId: 130000004, name: "Sun Shades", icon: Sun },
  { id: 130000405, parentId: 130000004, name: "Trunk & Cargo Organizers", icon: Package },

  /* Lights */
  { id: 130000501, parentId: 130000005, name: "Headlights & Bulbs", icon: Lightbulb },
  { id: 130000502, parentId: 130000005, name: "LED Light Bars", icon: Lightbulb },
  { id: 130000503, parentId: 130000005, name: "Fog Lights", icon: Lightbulb },
  { id: 130000504, parentId: 130000005, name: "Interior Ambient Lighting", icon: Lightbulb },

  /* Motorcycle */
  { id: 130000601, parentId: 130000006, name: "Helmets & Gear", icon: Bike },
  { id: 130000602, parentId: 130000006, name: "Motorcycle Parts", icon: Settings },
  { id: 130000603, parentId: 130000006, name: "Riding Jackets & Protective Wear", icon: ShieldCheck },
  { id: 130000604, parentId: 130000006, name: "ATV & UTV Accessories", icon: Bike },

  /* Oils */
  { id: 130000701, parentId: 130000007, name: "Engine Oil", icon: Droplets },
  { id: 130000702, parentId: 130000007, name: "Brake & Coolant Fluids", icon: Droplets },
  { id: 130000703, parentId: 130000007, name: "Transmission Fluid", icon: Droplets },
  { id: 130000704, parentId: 130000007, name: "Oil Filters", icon: Droplets },

  /* Paint */
  { id: 130000801, parentId: 130000008, name: "Car Paint", icon: Paintbrush },
  { id: 130000802, parentId: 130000008, name: "Touch-Up Paint", icon: Paintbrush },
  { id: 130000803, parentId: 130000008, name: "Clear Coats & Primers", icon: Paintbrush },
  { id: 130000804, parentId: 130000008, name: "Spray Guns & Paint Tools", icon: Paintbrush },

  /* Performance */
  { id: 130000901, parentId: 130000009, name: "Air Intakes", icon: Gauge },
  { id: 130000902, parentId: 130000009, name: "Exhaust Systems", icon: Flame },
  { id: 130000903, parentId: 130000009, name: "Turbochargers & Superchargers", icon: Gauge },
  { id: 130000904, parentId: 130000009, name: "ECU Tuners & Chips", icon: Settings },

  /* Replacement */
  { id: 130001001, parentId: 130000010, name: "Brake Parts", icon: Disc },
  { id: 130001002, parentId: 130000010, name: "Suspension Parts", icon: Settings },
  { id: 130001003, parentId: 130000010, name: "Belts & Hoses", icon: Settings },
  { id: 130001004, parentId: 130000010, name: "Filters (Air/Cabin/Fuel)", icon: Wind },

  /* RV Parts & Accessories */
  { id: 130001101, parentId: 130000011, name: "RV Covers", icon: Tent },
  { id: 130001102, parentId: 130000011, name: "RV Awnings", icon: Tent },
  { id: 130001103, parentId: 130000011, name: "RV Plumbing & Water Systems", icon: Droplets },
  { id: 130001104, parentId: 130000011, name: "RV Generators & Power", icon: Plug },

  /* Tires */
  { id: 130001201, parentId: 130000012, name: "Car Tires", icon: Disc },
  { id: 130001202, parentId: 130000012, name: "Alloy Wheels", icon: Disc },
  { id: 130001203, parentId: 130000012, name: "Tire Pressure Monitors", icon: Gauge },
  { id: 130001204, parentId: 130000012, name: "Wheel Accessories (Lugs, Caps)", icon: Disc },

  /* Tools */
  { id: 130001301, parentId: 130000013, name: "Garage Tools", icon: ToolCase },
  { id: 130001302, parentId: 130000013, name: "Diagnostic Tools", icon: Gauge },
  { id: 130001303, parentId: 130000013, name: "Hand Tools & Wrench Sets", icon: Wrench },
  { id: 130001304, parentId: 130000013, name: "Jacks & Lifts", icon: ToolCase },

  /* Automotive Enthusiast Merchandise */
  { id: 130001401, parentId: 130000014, name: "Car Model Toys & Diecast", icon: Car },
  { id: 130001402, parentId: 130000014, name: "Automotive Apparel", icon: ShoppingBag },
  { id: 130001403, parentId: 130000014, name: "Racing Collectibles", icon: Award },
  { id: 130001404, parentId: 130000014, name: "Garage Signs & Decor", icon: ShoppingBag },

  /* Heavy Duty & Commercial Vehicle Equipment */
  { id: 130001501, parentId: 130000015, name: "Truck Bed Accessories", icon: Truck },
  { id: 130001502, parentId: 130000015, name: "Trailer Hitches & Towing", icon: Anchor },
  { id: 130001503, parentId: 130000015, name: "Commercial Vehicle Lighting", icon: Lightbulb },
  { id: 130001504, parentId: 130000015, name: "Fleet Maintenance Equipment", icon: ToolCase },

  /* Amazon Autos */
  { id: 130001601, parentId: 130000016, name: "New Vehicles", icon: Car },
  { id: 130001602, parentId: 130000016, name: "Used Vehicles", icon: Car },
  { id: 130001603, parentId: 130000016, name: "Vehicle Financing & Warranty", icon: ShieldCheck },
  { id: 130001604, parentId: 130000016, name: "Vehicle History & Inspection", icon: ShieldCheck },

  /* Towing & Trailer Equipment */
  { id: 130001701, parentId: 130000017, name: "Tow Hitches", icon: Anchor },
  { id: 130001702, parentId: 130000017, name: "Trailer Wiring & Lighting", icon: Lightbulb },
  { id: 130001703, parentId: 130000017, name: "Cargo Trailers & Carriers", icon: Truck },

  /* Fuel Systems & Additives */
  { id: 130001801, parentId: 130000018, name: "Fuel Injectors & Pumps", icon: Fuel },
  { id: 130001802, parentId: 130000018, name: "Fuel Additives & Treatments", icon: Fuel },
  { id: 130001803, parentId: 130000018, name: "Fuel Tanks & Cans", icon: Fuel },

  /* Climate & Comfort (HVAC) */
  { id: 130001901, parentId: 130000019, name: "AC Compressors & Kits", icon: Snowflake },
  { id: 130001902, parentId: 130000019, name: "Cabin Heaters & Defrosters", icon: Wind },
  { id: 130001903, parentId: 130000019, name: "Refrigerant & Recharge Kits", icon: Snowflake },

  /* Batteries & Charging Systems */
  { id: 130002001, parentId: 130000020, name: "Car Batteries", icon: BatteryCharging },
  { id: 130002002, parentId: 130000020, name: "Battery Chargers & Jump Starters", icon: Plug },
  { id: 130002003, parentId: 130000020, name: "Alternators & Starters", icon: BatteryCharging },
];

/* ──────────────────────────────────────────────
   3️⃣ PRODUCTS (1300101xx+)
   ────────────────────────────────────────────── */
export const automotiveProducts: CategoryItem[] = [
  /* Car Care */
  { id: 130010101, parentId: 130000101, name: "pH Neutral Car Shampoo", icon: Droplets },
  { id: 130010102, parentId: 130000102, name: "Ceramic Wax Polish", icon: Droplets },
  { id: 130010103, parentId: 130000103, name: "Foaming Interior Cleaner", icon: Package },
  { id: 130010104, parentId: 130000104, name: "10-Piece Detailing Kit", icon: Sparkles },
  { id: 130010105, parentId: 130000105, name: "Premium Microfiber Towel Set", icon: Layers },

  /* Electronics */
  { id: 130020101, parentId: 130000201, name: "Android Car Stereo", icon: Radio },
  { id: 130020201, parentId: 130000202, name: "4K Dash Camera", icon: Gauge },
  { id: 130020301, parentId: 130000203, name: "Fast-Charge Phone Mount", icon: BatteryCharging },
  { id: 130020401, parentId: 130000204, name: "Wireless Backup Camera Kit", icon: Gauge },
  { id: 130020501, parentId: 130000205, name: "7-Inch GPS Navigator", icon: Radio },

  /* Exterior */
  { id: 130030101, parentId: 130000301, name: "Waterproof Car Cover", icon: Car },
  { id: 130030201, parentId: 130000302, name: "Aluminum Roof Rack Crossbars", icon: Truck },
  { id: 130030301, parentId: 130000303, name: "Molded Splash Guards", icon: Car },
  { id: 130030401, parentId: 130000304, name: "Front Bumper Guard", icon: Car },
  { id: 130030501, parentId: 130000305, name: "Rain Guard Window Visors", icon: Wind },

  /* Interior */
  { id: 130040101, parentId: 130000401, name: "Neoprene Seat Cover Set", icon: Package },
  { id: 130040201, parentId: 130000402, name: "All-Weather Floor Liners", icon: Package },
  { id: 130040301, parentId: 130000403, name: "Leather Steering Wheel Cover", icon: Package },
  { id: 130040401, parentId: 130000404, name: "Reflective Windshield Sun Shade", icon: Sun },
  { id: 130040501, parentId: 130000405, name: "Collapsible Trunk Organizer", icon: Package },

  /* Lights */
  { id: 130050101, parentId: 130000501, name: "LED Headlight Bulbs", icon: Lightbulb },
  { id: 130050201, parentId: 130000502, name: "20-Inch LED Light Bar", icon: Lightbulb },
  { id: 130050301, parentId: 130000503, name: "LED Fog Light Kit", icon: Lightbulb },
  { id: 130050401, parentId: 130000504, name: "RGB Interior LED Strip Kit", icon: Lightbulb },

  /* Motorcycle */
  { id: 130060101, parentId: 130000601, name: "Full-Face Helmet", icon: Bike },
  { id: 130060201, parentId: 130000602, name: "Motorcycle Chain & Sprocket Kit", icon: Settings },
  { id: 130060301, parentId: 130000603, name: "Armored Riding Jacket", icon: ShieldCheck },
  { id: 130060401, parentId: 130000604, name: "ATV Winch Kit", icon: Bike },

  /* Oils */
  { id: 130070101, parentId: 130000701, name: "5W-30 Engine Oil", icon: Droplets },
  { id: 130070201, parentId: 130000702, name: "DOT 4 Brake Fluid", icon: Droplets },
  { id: 130070301, parentId: 130000703, name: "Automatic Transmission Fluid", icon: Droplets },
  { id: 130070401, parentId: 130000704, name: "High-Efficiency Oil Filter", icon: Droplets },

  /* Paint */
  { id: 130080101, parentId: 130000801, name: "Automotive Spray Paint", icon: Paintbrush },
  { id: 130080201, parentId: 130000802, name: "OEM-Match Touch-Up Pen", icon: Paintbrush },
  { id: 130080301, parentId: 130000803, name: "Clear Coat Aerosol", icon: Paintbrush },
  { id: 130080401, parentId: 130000804, name: "HVLP Spray Gun", icon: Paintbrush },

  /* Performance */
  { id: 130090101, parentId: 130000901, name: "Cold Air Intake Kit", icon: Gauge },
  { id: 130090201, parentId: 130000902, name: "Cat-Back Exhaust System", icon: Flame },
  { id: 130090301, parentId: 130000903, name: "Bolt-On Turbocharger Kit", icon: Gauge },
  { id: 130090401, parentId: 130000904, name: "Performance ECU Tuner", icon: Settings },

  /* Replacement */
  { id: 130100101, parentId: 130001001, name: "Ceramic Brake Pad Set", icon: Disc },
  { id: 130100201, parentId: 130001002, name: "Front Strut Assembly", icon: Settings },
  { id: 130100301, parentId: 130001003, name: "Serpentine Belt", icon: Settings },
  { id: 130100401, parentId: 130001004, name: "Engine Cabin Air Filter", icon: Wind },

  /* RV */
  { id: 130110101, parentId: 130001101, name: "Heavy-Duty RV Cover", icon: Tent },
  { id: 130110201, parentId: 130001102, name: "Retractable RV Awning", icon: Tent },
  { id: 130110301, parentId: 130001103, name: "RV Water Pump Kit", icon: Droplets },
  { id: 130110401, parentId: 130001104, name: "Portable RV Generator", icon: Plug },

  /* Tires */
  { id: 130120101, parentId: 130001201, name: "All-Season Car Tires", icon: Disc },
  { id: 130120201, parentId: 130001202, name: "17-Inch Alloy Wheel Set", icon: Disc },
  { id: 130120301, parentId: 130001203, name: "Bluetooth TPMS Sensor Kit", icon: Gauge },
  { id: 130120401, parentId: 130001204, name: "Chrome Lug Nut Set", icon: Disc },

  /* Tools */
  { id: 130130101, parentId: 130001301, name: "Hydraulic Jack", icon: ToolCase },
  { id: 130130201, parentId: 130001302, name: "OBD2 Diagnostic Scanner", icon: Gauge },
  { id: 130130301, parentId: 130001303, name: "120-Piece Wrench & Socket Set", icon: Wrench },
  { id: 130130401, parentId: 130001304, name: "2-Post Car Lift", icon: ToolCase },

  /* Enthusiast Merchandise */
  { id: 130140101, parentId: 130001401, name: "1:18 Scale Diecast Model Car", icon: Car },
  { id: 130140201, parentId: 130001402, name: "Racing Team T-Shirt", icon: ShoppingBag },
  { id: 130140301, parentId: 130001403, name: "Signed Racing Helmet Replica", icon: Award },
  { id: 130140401, parentId: 130001404, name: "Vintage Garage Metal Sign", icon: ShoppingBag },

  /* Heavy Duty & Commercial */
  { id: 130150101, parentId: 130001501, name: "Truck Bed Liner", icon: Truck },
  { id: 130150201, parentId: 130001502, name: "Class III Trailer Hitch", icon: Anchor },
  { id: 130150301, parentId: 130001503, name: "LED Commercial Beacon Light", icon: Lightbulb },
  { id: 130150401, parentId: 130001504, name: "Fleet Tire Inflator Station", icon: ToolCase },

  /* Amazon Autos */
  { id: 130160101, parentId: 130001601, name: "2026 Compact SUV Listing", icon: Car },
  { id: 130160201, parentId: 130001602, name: "Certified Pre-Owned Sedan Listing", icon: Car },
  { id: 130160301, parentId: 130001603, name: "Extended Vehicle Warranty Plan", icon: ShieldCheck },
  { id: 130160401, parentId: 130001604, name: "Vehicle History Report", icon: ShieldCheck },

  /* Towing & Trailer */
  { id: 130170101, parentId: 130001701, name: "2-Inch Receiver Hitch", icon: Anchor },
  { id: 130170201, parentId: 130001702, name: "7-Way Trailer Wiring Harness", icon: Lightbulb },
  { id: 130170301, parentId: 130001703, name: "Enclosed Cargo Trailer", icon: Truck },

  /* Fuel Systems */
  { id: 130180101, parentId: 130001801, name: "Electric Fuel Pump", icon: Fuel },
  { id: 130180201, parentId: 130001802, name: "Fuel System Cleaner", icon: Fuel },
  { id: 130180301, parentId: 130001803, name: "5-Gallon Fuel Can", icon: Fuel },

  /* Climate & Comfort */
  { id: 130190101, parentId: 130001901, name: "AC Compressor Repair Kit", icon: Snowflake },
  { id: 130190201, parentId: 130001902, name: "12V Cabin Heater", icon: Wind },
  { id: 130190301, parentId: 130001903, name: "R-134a Refrigerant Recharge Kit", icon: Snowflake },

  /* Batteries & Charging */
  { id: 130200101, parentId: 130002001, name: "AGM Car Battery", icon: BatteryCharging },
  { id: 130200201, parentId: 130002002, name: "Portable Jump Starter", icon: Plug },
  { id: 130200301, parentId: 130002003, name: "High-Output Alternator", icon: BatteryCharging },
];

/* ──────────────────────────────────────────────
   4️⃣ COMBINED / DERIVED EXPORTS
   ────────────────────────────────────────────── */

/** Every automotive item (categories + subcategories + products) in one flat array. */
export const allAutomotiveItems: CategoryItem[] = [
  ...automotiveCategories,
  ...automotiveSubcategories,
  ...automotiveProducts,
];

/** Fast id -> item lookup map, built once. */
export const automotiveItemsById: Map<number, CategoryItem> = new Map(
  allAutomotiveItems.map((item) => [item.id, item])
);

/** Get a single item by id, or undefined if not found. */
export function getAutomotiveItemById(id: number): CategoryItem | undefined {
  return automotiveItemsById.get(id);
}

/** Get the direct children of a given parent id (subcategories of a category, or products of a subcategory). */
export function getAutomotiveChildren(parentId: number): CategoryItem[] {
  return allAutomotiveItems.filter((item) => item.parentId === parentId);
}

/** Get the breadcrumb trail (root -> ... -> item) for a given item id. */
export function getAutomotiveBreadcrumb(id: number): CategoryItem[] {
  const trail: CategoryItem[] = [];
  let current = automotiveItemsById.get(id);
  while (current) {
    trail.unshift(current);
    current = current.parentId != null ? automotiveItemsById.get(current.parentId) : undefined;
  }
  return trail;
}

/** Nested tree node shape used by buildAutomotiveCategoryTree. */
export interface CategoryTreeNode extends CategoryItem {
  children: CategoryTreeNode[];
}

/** Build a fully nested tree (categories -> subcategories -> products) for rendering menus. */
export function buildAutomotiveCategoryTree(): CategoryTreeNode[] {
  const nodeMap = new Map<number, CategoryTreeNode>(
    allAutomotiveItems.map((item) => [item.id, { ...item, children: [] }])
  );
  const roots: CategoryTreeNode[] = [];

  nodeMap.forEach((node) => {
    if (node.parentId != null && nodeMap.has(node.parentId)) {
      nodeMap.get(node.parentId)!.children.push(node);
    } else {
      roots.push(node);
    }
  });

  return roots;
}

/** Simple case-insensitive search across all automotive items by name. */
export function searchAutomotiveItems(query: string): CategoryItem[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  return allAutomotiveItems.filter((item) => item.name.toLowerCase().includes(q));
}

/** Counts for quick dashboard/summary use. */
export const automotiveCounts = {
  categories: automotiveCategories.length,
  subcategories: automotiveSubcategories.length,
  products: automotiveProducts.length,
  total: allAutomotiveItems.length,
};
